import type { ReactNode } from 'react';
import {
  ArchiveIcon,
  ClipboardCheckIcon,
  LinkIcon,
  MoveIcon,
  UploadIcon
} from './icons';
import { useDraggableChrome } from './useDraggableChrome';
import '../styles/rail-drag.css';

export type RailPanelId = 'library' | 'upload' | 'link' | 'submissions';

export interface RailItem {
  id: RailPanelId;
  /** Rail caption - all nouns so the column reads as places, not verbs. */
  label: string;
  /** Panel header title for the same id, so the rail and the panel cannot disagree. */
  title: string;
  icon: ReactNode;
}

export const RAIL_ITEMS: RailItem[] = [
  { id: 'library', label: 'Kho bài', title: 'Kho bài', icon: <ArchiveIcon size={24} /> },
  { id: 'upload', label: 'Tải lên', title: 'Tải lên', icon: <UploadIcon size={24} /> },
  { id: 'link', label: 'Chèn link', title: 'Chèn link', icon: <LinkIcon size={24} /> },
  { id: 'submissions', label: 'Bài nộp', title: 'Bài nộp của học sinh', icon: <ClipboardCheckIcon size={24} /> },
];

export function railPanelTitle(id: RailPanelId): string {
  return RAIL_ITEMS.filter((item) => item.id === id)[0].title;
}

export interface LeftRailProps {
  activeId?: RailPanelId;
  /** Floating card over the canvas, or a full-height column once a panel is open. */
  docked?: boolean;
  onSelect?: (id: RailPanelId) => void;
}

export function LeftRail({ activeId, docked = false, onSelect }: LeftRailProps) {
  // only the floating card moves; the docked column is anchored, and keeps the saved spot for later
  const drag = useDraggableChrome({ id: 'left-rail', enabled: !docked });

  const className = [
    docked ? 'rail rail--docked' : 'rail rail--floating',
    drag.dragging ? 'rail--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <nav ref={drag.ref} className={className} aria-label="Công cụ lớp học">
      {/* hidden by shell.css when docked */}
      <button
        type="button"
        className="rail__handle"
        aria-label="Kéo để di chuyển thanh công cụ, dùng phím mũi tên để chỉnh vị trí"
        {...drag.handleProps}
      >
        <MoveIcon size={18} />
      </button>
      {RAIL_ITEMS.map((item) => (
        <button
          key={item.id}
          type="button"
          className={
            item.id === activeId ? 'rail__item rail__item--active' : 'rail__item'
          }
          aria-expanded={item.id === activeId}
          aria-current={item.id === activeId ? 'true' : undefined}
          onClick={onSelect ? () => onSelect(item.id) : undefined}
        >
          <span className="rail__icon" aria-hidden={true}>{item.icon}</span>
          <span className="rail__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}
